import { useGenerateReportPdfMutation } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { generateZipFileFromUrls } from '@/utils';

const BATCH_SIZE = 5;

function chunk<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

export const useBulkReportDownload = ({
  selectedReportIds,
  setLoading,
}: {
  selectedReportIds: string[];
  setLoading: (loading: boolean) => void;
}) => {
  const [generateReportPdf] = useGenerateReportPdfMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled', isReportDownload: true });

  const handleBulkDownload = async () => {
    if (selectedReportIds.length === 0) return;
    if (selectedReportIds.length > BATCH_SIZE) {
      showSnackBar(`You can download up to ${BATCH_SIZE} reports at a time.`, 'info');
      return;
    }
    try {
      setLoading(true);

      showSnackBar(
        'Preparing your reports. Download will start once ready, you can continue working.',
        'info',
        { persistent: true }
      );

      const chunks = chunk(selectedReportIds, BATCH_SIZE);
      const allDownloadUrls: string[] = [];
      const allFilenames: string[] = [];
      let successCount = 0;
      let failCount = 0;

      for (const batchIds of chunks) {
        try {
          const { data } = await generateReportPdf({
            variables: { reportIds: batchIds },
          });

          if (
            data?.generateReportPdf?.downloadUrl &&
            data.generateReportPdf.downloadUrl.length > 0
          ) {
            const urls = data.generateReportPdf.downloadUrl.filter(Boolean) as string[];
            const filenames = (data.generateReportPdf.filename?.filter(Boolean) as string[]) ?? [];
            const resolvedFilenames = urls.map((_, i) => filenames[i] ?? `report_${i + 1}.pdf`);
            allDownloadUrls.push(...urls);
            allFilenames.push(...resolvedFilenames);
            successCount += batchIds.length;
          } else {
            failCount += batchIds.length;
          }
        } catch {
          failCount += batchIds.length;
        }
      }

      const total = selectedReportIds.length;

      if (allDownloadUrls.length > 0) {
        showSnackBar('Your reports are ready. Download is starting...', 'success');

        setTimeout(async () => {
          if (allDownloadUrls.length === 1) {
            window.open(allDownloadUrls[0], '_blank');
          } else {
            await generateZipFileFromUrls(allDownloadUrls, allFilenames);
          }

          setTimeout(() => {
            if (failCount === 0) {
              showSnackBar(
                `${successCount} report${successCount === 1 ? '' : 's'} downloaded successfully.`,
                'success'
              );
            } else {
              showSnackBar(
                `${successCount} of ${total} report${total === 1 ? '' : 's'} downloaded successfully. ${failCount} failed.`,
                'warning'
              );
            }
          }, 500);
        }, 2000);
      } else {
        showSnackBar(
          failCount === total
            ? 'Download failed. Please try again.'
            : `${failCount} of ${total} report${total === 1 ? '' : 's'} failed to download.`,
          'error'
        );
      }
    } catch (error) {
      showSnackBar('Download failed. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return {
    handleBulkDownload,
  };
};
